import i18n from 'i18next';
import { defaultThemes, LANGUAGES, localStorageKeys } from '../../utils/Constants';
import { applyFontFamily } from '../../utils';

const reservationFlowStorage = localStorage.getItem(localStorageKeys.RESERVATION_FLOW);
const initReservationFlow = reservationFlowStorage ? JSON.parse(reservationFlowStorage) : { currentIndex: 0 };

const initialState = {
    theme: localStorage.getItem('theme') ?? 'light',
    purchasedActiveKey: '1',
    activeThemeIndex: 0,
    ticketThemes: [
        {
            backgroundImage: null,
            backgroundColor: defaultThemes.backgroundColor1,
            colorFontTop: defaultThemes.colorFontTicket1Top,
            colorFontCenter: defaultThemes.colorFontTicket1Center,
            colorFontBottom: defaultThemes.colorFontTicket1Bottom,
        },
        {
            backgroundImage: null,
            backgroundColor: defaultThemes.backgroundColor2,
            colorFontTop: defaultThemes.colorFontTicket2Top,
            colorFontCenter: defaultThemes.colorFontTicket2Center,
            colorFontBottom: defaultThemes.colorFontTicket2Bottom,
        },
    ], // チケットごとのテーマ一覧
    currentTheme: {
        backgroundImage: null,
        backgroundColor: defaultThemes.backgroundColor1,
        colorFontTop: defaultThemes.colorFontTicket1Top,
        colorFontCenter: defaultThemes.colorFontTicket1Center,
        colorFontBottom: defaultThemes.colorFontTicket1Bottom,
    }, // 現在表示しているチケットのテーマ
    currentOrderId: '', // 現在注目しているチケットのorderId
    isActiveTicketShown: false, //現在チケットのくるくるを表示しているか。
    currentTicket: {}, // 現在のチケット情報
    serviceWorkerUpdated: false, //サービスワーカーフラグ
    serviceWorkerRegistration: null,
    serverMaintenance: {
        isMaintenance: false,
        endAt: null,
    },
    reservationFlow: initReservationFlow,
};

export interface Theme {
    backgroundImage: string | null;
    backgroundColor: string;
    colorFontTop: string;
    colorFontCenter: string;
    colorFontBottom: string;
}

interface ServerMaintenance {
    isMaintenance: boolean;
    endAt: string | null;
}

interface ReservationFlow {
    currentIndex: number;
}

export interface RootState {
    theme: string;
    purchasedActiveKey: string;
    activeThemeIndex: number;
    ticketThemes: Theme[];
    currentTheme: Theme;
    currentOrderId: string;
    isActiveTicketShown: boolean;
    currentTicket: Record<string, any>;
    serviceWorkerUpdated: boolean;
    serviceWorkerRegistration: ((reloadPage?: boolean) => Promise<void>) | null;
    serverMaintenance: ServerMaintenance;
    reservationFlow: ReservationFlow;
    language?: string;
}

interface Action<T = any> {
    type: string;
    payload?: T;
}

export const rootReducer = (state: RootState = initialState, action: Action): RootState => {
    // console.log({state}, {action})
    switch (action.type) {
        case 'CHANGE_THEME':
            localStorage.setItem('theme', action.payload);
            return { ...state, theme: action.payload };
        case 'CHANGE_LANGUAGE':
            void i18n.changeLanguage(action.payload); //呼び出し
            applyFontFamily(LANGUAGES[action.payload].fontFamily);
            return { ...state, language: action.payload };
        case 'CHANGE_PURCHASED_ACTIVEKEY': //購入済みチケットリストのうち、使用前,使用中,使用済みのどの画面を表示するか。
            return { ...state, purchasedActiveKey: action.payload };
        case 'CHANGE_ACTIVE_THEME': {
            //有効化画面でタップした時に、テーマを変更する。
            let index = state.activeThemeIndex;
            if (index + 1 < state.ticketThemes.length) {
                index = index + 1;
            } else {
                index = 0;
            }
            return { ...state, activeThemeIndex: index, currentTheme: state.ticketThemes[index] };
        }
        case 'CHANGE_CURRENT_ORDER_ID': //現在注目しているチケットのorderIdを変更する。
            return { ...state, currentOrderId: action.payload };
        case 'CHANGE_IS_ACTIVE_TICKET_SHOWN': // 現在有効化券面（くるくる）を表示しているかどうか。
            return { ...state, isActiveTicketShown: action.payload };
        case 'SET_CURRENT_TICKET': {
            //現在注目しているチケットを変更する。
            const orderTicket = action.payload;
            if (!orderTicket) {
                return state;
            }

            const themes: Theme[] = [
                {
                    backgroundImage: orderTicket?.backgroundImage1 ?? null,
                    backgroundColor: defaultThemes.backgroundColor1,
                    colorFontTop: orderTicket?.colorFontTicket1Top ?? defaultThemes.colorFontTicket1Top,
                    colorFontCenter: orderTicket?.colorFontTicket1Center ?? defaultThemes.colorFontTicket1Center,
                    colorFontBottom: orderTicket?.colorFontTicket1Bottom ?? defaultThemes.colorFontTicket1Bottom,
                },
                {
                    backgroundImage: orderTicket?.backgroundImage2 ?? null,
                    backgroundColor: defaultThemes.backgroundColor2,
                    colorFontTop: orderTicket?.colorFontTicket2Top ?? defaultThemes.colorFontTicket2Top,
                    colorFontCenter: orderTicket?.colorFontTicket2Center ?? defaultThemes.colorFontTicket2Center,
                    colorFontBottom: orderTicket?.colorFontTicket2Bottom ?? defaultThemes.colorFontTicket2Bottom,
                },
            ];

            return {
                ...state,
                currentTicket: orderTicket,
                currentOrderId: orderTicket.orderId,
                ticketThemes: themes,
                currentTheme: themes[0],
                activeThemeIndex: 0,
            };
        }
        case 'REMOVE_CURRENT_TICKET': //チケットを注目から外す。
            return {
                ...state,
                currentOrderId: initialState.currentOrderId,
                ticketThemes: initialState.ticketThemes,
                currentTheme: initialState.currentTheme,
                activeThemeIndex: initialState.activeThemeIndex,
                currentTicket: initialState.currentTicket,
            };
        case 'SW_UPDATE':
            return {
                ...state,
                serviceWorkerUpdated: true,
                serviceWorkerRegistration: action.payload,
            };
        case 'SET_DISPLAY_SETTINGS':
            // console.log('set display settings', action.payload);
            return {
                ...state,
                ticketThemes: [action.payload.theme1, action.payload.theme2],
                currentTheme: action.payload.theme1,
                activeThemeIndex: 0,
            };
        case 'SERVER_MAINTENANCE':
            return {
                ...state,
                serverMaintenance: action.payload,
            };

        case 'RESERVATION_FLOW': {
            const newState = {
                ...state,
                reservationFlow: {
                    ...state.reservationFlow,
                    ...action.payload,
                },
            };
            localStorage.setItem(localStorageKeys.RESERVATION_FLOW, JSON.stringify(newState.reservationFlow));
            return newState;
        }

        default:
            break;
    }
    return state;
};
